import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from '../../firebase.config';
import { getCollectionForCompany } from '../../utils/dataAdapter';
import { useTableSort, SortableHeader } from '../../utils/tableSort';
import { useTablePagination } from '../../utils/tablePagination';
import PaginationControls from '../../utils/PaginationControls';
import GlobalExportButtons from '../GlobalExportButtons';
import { Calendar, TrendingDown, TrendingUp } from 'lucide-react';
import { CollapsibleQuickTotals } from './CommonComponents';

// Clean rebuild: Cash Flow + Daybook
// Mapping rules
// - Payment In: payments with receipt number prefix PRI or type === 'receipt'
// - Payment Out: payments with receipt number prefix PRP or type === 'payment'
// - Also Outflow: expenses and salaryPayments
// - Strict period filter (>= start, <= end). Opening balance fixed at 0 for the period.

const CashFlowReport = ({ db, userId, appId, companyId, dateRange, loading, setLoading, companyDetails, txnFilter, setTxnFilter }) => {
	const [rows, setRows] = useState([]);
	const [viewMode, setViewMode] = useState('cash'); // 'cash' | 'daybook'


	const normalizeDate = (raw) => {
		if (!raw) return '';
		if (typeof raw === 'string' && raw.includes('/')) {
			const [dd, mm, yyyy] = raw.split('/');
			if (dd && mm && yyyy) return `${yyyy}-${String(mm).padStart(2,'0')}-${String(dd).padStart(2,'0')}`;
		}
		return raw;
	};

	const isInflow = (d) => {
		const rec = (d.receiptNumber || d.number || '').toString().toUpperCase();
		const t = String(d.type || '').toLowerCase();
		const docT = String(d.documentType || '').toLowerCase();
		// Check for PRP first (more specific), then PRI
		// Handle cases where receiptNumber might be "PRI PRP25-26/18" - prioritize PRP
		const hasPRP = rec.includes('PRP') && (rec.startsWith('PRP') || rec.includes(' PRP'));
		if (hasPRP) return false; // PRP is outflow, not inflow
		return rec.startsWith('PRI') || t === 'receipt' || docT === 'sale' || docT === 'sales';
	};
	const isOutflow = (d) => {
		const rec = (d.receiptNumber || d.number || '').toString().toUpperCase();
		const t = String(d.type || '').toLowerCase();
		const docT = String(d.documentType || '').toLowerCase();
		// Check for PRP (either at start or after space like "PRI PRP25-26/18")
		return (rec.includes('PRP') && (rec.startsWith('PRP') || rec.includes(' PRP'))) || t === 'payment' || docT === 'purchase' || docT === 'expense';
	};
	const modeOf = (m) => (String(m || '').toLowerCase().includes('cash') ? 'cash' : 'bank');

  useEffect(() => {
		const fetchData = async () => {
      if (!db || !userId || !appId || !companyId) return;
      setLoading(true);
      try {
				// Default range: last 7 days including today, if not provided by parent
				const today = new Date();
				const d7 = new Date(today); d7.setDate(today.getDate() - 6);
				const startStr = new Date(dateRange?.start || d7).toISOString().split('T')[0];
				const endStr = new Date(dateRange?.end || today).toISOString().split('T')[0];



				// Payments within period
				const pq = query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'payments'),
					where('paymentDate', '>=', startStr), where('paymentDate', '<=', endStr), orderBy('paymentDate', 'asc')
				);
				const ps = await getDocs(pq);
				const payRows = ps.docs.map(d => {
					const v = d.data();
					const date = normalizeDate(v.paymentDate || v.date);
					const inflow = isInflow(v) ? Number(v.totalAmount || v.amount || 0) : 0;
					const outflow = isOutflow(v) ? Number(v.totalAmount || v.amount || 0) : 0;
					const docNo = v.receiptNumber || v.number || (inflow > 0 ? 'Receipt' : 'Payment');
					return inflow > 0 || outflow > 0 ? {
						id: `pay:${d.id}`,
						date,
						type: inflow > 0 ? 'Receipt' : 'Payment',
						inflow,
						outflow,
						docNo,
						partyName: v.partyName || v.partyFirmName || v.employeeName || '',
						mode: modeOf(v.paymentMode)
					} : null;
				}).filter(Boolean);

				// Expenses within period (Outflow)
				const eq = query(
					getCollectionForCompany(db, { companyId, appId, userId }, 'expenses'),
					where('date', '>=', startStr), where('date', '<=', endStr), orderBy('date', 'asc')
				);
				const es = await getDocs(eq);
				const expRows = es.docs.map(d => {
					const v = d.data();
          return {
						id: `exp:${d.id}`,
						date: normalizeDate(v.date),
						type: 'Expense',
						inflow: 0,
						outflow: Number(v.amount || 0),
						docNo: v.expenseNumber || v.voucherNumber || v.referenceNo || v.head || v.category || 'Expense',
						partyName: v.partyName || v.head || v.category || '',
						mode: modeOf(v.paymentMode)
					};
				}).filter(r => r.outflow > 0);

				// Salary payments within period (Outflow)
				const sq = query(
					getCollectionForCompany(db, { companyId, appId, userId }, 'salaryPayments'),
					where('date', '>=', startStr), where('date', '<=', endStr), orderBy('date', 'asc')
				);
				const ss = await getDocs(sq);
				const salRows = ss.docs.map(d => {
					const v = d.data();
          return {
						id: `sal:${d.id}`,
						date: normalizeDate(v.date || v.paymentDate),
						type: 'Salary',
						inflow: 0,
						outflow: Number(v.netAmount || v.total || 0),
						docNo: v.month || v.employeeName || 'Salary',
						partyName: v.employeeName || 'Employee',
						mode: modeOf(v.paymentMode)
					};
				}).filter(r => r.outflow > 0);

				// Irregular labour/freelancer payments (Outflow)
				const iq = query(
					getCollectionForCompany(db, { companyId, appId, userId }, 'irregularPayments'),
					where('date', '>=', startStr), where('date', '<=', endStr), orderBy('date', 'asc')
				);
				const isnap = await getDocs(iq);
				const irrRows = isnap.docs.map(d => {
					const v = d.data();
					return {
						id: `irr:${d.id}`,
						date: normalizeDate(v.date),
						type: 'Labour/Advance',
						inflow: 0,
						outflow: Number(v.amount || 0),
						docNo: v.paymentType || 'Irregular',
						partyName: v.personType === 'Employee' ? (v.employeeName || 'Employee') : (v.personName || v.personType || ''),
						mode: modeOf(v.paymentMode)
					};
				}).filter(r => r.outflow > 0);

				// Tax payments under Taxes module (Outflow)
				const tq = query(
					getCollectionForCompany(db, { companyId, appId, userId }, 'taxPayments'),
					where('paymentDate', '>=', startStr), where('paymentDate', '<=', endStr), orderBy('paymentDate', 'asc')
				);
				const ts = await getDocs(tq);
				const taxRows = ts.docs.map(d => {
					const v = d.data();
					return {
						id: `tax:${d.id}`,
						date: normalizeDate(v.paymentDate),
						type: 'Tax',
          inflow: 0,
						outflow: Number(v.amount || 0),
						docNo: `${v.taxType||'TAX'} ${v.periodValue||''} ${v.reference||''}`.trim(),
						partyName: 'Tax',
						mode: modeOf(v.paymentMode)
					};
				}).filter(r => r.outflow > 0);

				const all = [...payRows, ...expRows, ...salRows, ...irrRows, ...taxRows].sort((a, b) => new Date(a.date) - new Date(b.date));
				let bal = 0;
				const withBal = all.map(r => { 
					bal = Math.round((bal + (r.inflow || 0) - (r.outflow || 0)) * 100) / 100; 
					return { ...r, balance: bal }; 
				});
				setRows(withBal);
			} catch (e) {
				console.error('CashFlow fetch error:', e);
				setRows([]);
      } finally {
        setLoading(false);
      }
    };
		fetchData();
	}, [db, userId, appId, companyId, dateRange]);

	// Sorting and pagination (filter before pagination)
	const { sortedData, sortConfig, handleSort } = useTableSort(rows, { key: 'date', direction: 'asc' });
	const filtered = sortedData.filter(r => txnFilter === 'all' ? true : r.mode === txnFilter);
	const pagination = useTablePagination(filtered, 25);

	const formatCurrency = (n) => new Intl.NumberFormat('en-IN', { 
    style: 'currency', 
    currency: 'INR', 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }).format(n || 0);
	const formatDate = (d) => new Date(d).toLocaleDateString('en-IN');

	const totals = filtered.reduce((acc, r) => ({ 
		inflow: Math.round((acc.inflow + (r.inflow || 0)) * 100) / 100, 
		outflow: Math.round((acc.outflow + (r.outflow || 0)) * 100) / 100 
	}), { inflow: 0, outflow: 0 });
	const summary = { 
		inflow: Math.round(totals.inflow * 100) / 100, 
		outflow: Math.round(totals.outflow * 100) / 100, 
		net: Math.round((totals.inflow - totals.outflow) * 100) / 100, 
		closing: Math.round((totals.inflow - totals.outflow) * 100) / 100 
	};

	// Helpers for exports/print/share
	const effectiveRange = (() => {
		const today = new Date();
		const d7 = new Date(today); d7.setDate(today.getDate() - 6);
		const s = new Date(dateRange?.start || d7);
		const e = new Date(dateRange?.end || today);
		return { start: s, end: e };
	})();

	const buildExportRows = () => filtered.map(r => ({
		date: formatDate(r.date),
		docNo: r.docNo || '',
		type: r.type,
		party: r.partyName || '',
		inflow: Math.round((r.inflow || 0) * 100) / 100,
		outflow: Math.round((r.outflow || 0) * 100) / 100,
		balance: Math.round((r.balance || 0) * 100) / 100
	}));

	const columnsDef = [
		{ key: 'date', label: 'Date', width: 26 },
		{ key: 'docNo', label: 'Doc No', width: 36 },
		{ key: 'type', label: 'Type', width: 22 },
		{ key: 'party', label: 'Party', width: 40 },
		{ key: 'inflow', label: 'Inflow', width: 24 },
		{ key: 'outflow', label: 'Outflow', width: 24 },
		{ key: 'balance', label: 'Balance', width: 26 }
	];

	// Prepare export data for GlobalExportButtons
	const getExportData = () => buildExportRows();

	const getExportColumns = () => columnsDef;

	const getReportDetails = () => ({
		'Period': `${effectiveRange.start.toLocaleDateString('en-IN')} to ${effectiveRange.end.toLocaleDateString('en-IN')}`,
		'Inflow': summary.inflow,
		'Outflow': summary.outflow,
		'Net': summary.net,
		dateRange: { start: effectiveRange.start, end: effectiveRange.end }
	});

  return (
    <div className="p-3 sm:p-6">
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Cash Flow / Daybook</h2>
            <p className="text-gray-600">Period: {formatDate(dateRange.start)} to {formatDate(dateRange.end)}</p>
          </div>
          
          {/* Global Export/Print/Share Buttons */}
          <GlobalExportButtons
            data={getExportData()}
            columns={getExportColumns()}
            filename="CASHFLOW"
            title="Cash Flow / Daybook"
            companyDetails={companyDetails}
            reportDetails={getReportDetails()}
            disabled={filtered.length === 0}
          />
        </div>
      </div>


			{viewMode === 'cash' && (
				<CollapsibleQuickTotals
					cards={[
						{
							label: 'Total Inflow',
							value: formatCurrency(summary.inflow),
							color: 'green'
						},
						{
							label: 'Total Outflow',
							value: formatCurrency(summary.outflow),
							color: 'red'
						},
						{
							label: 'Net Cash Flow',
							value: formatCurrency(summary.net),
							color: 'blue'
						},
						{
							label: 'Opening Balance',
							value: formatCurrency(0),
							color: 'yellow'
						},
						{
							label: 'Closing Balance',
							value: formatCurrency(summary.closing),
							color: 'purple'
						}
					]}
				/>
			)}

        <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-sky-50 via-white to-amber-50 hidden md:block">
            <h3 className="text-sm font-bold text-gray-900">Transaction register</h3>
            <p className="text-xs text-gray-500 mt-0.5">Sort columns below; export from the header.</p>
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
								<SortableHeader columnKey="date" label="Date" onSort={handleSort} sortConfig={sortConfig} />
								<SortableHeader columnKey="docNo" label="Doc No" onSort={handleSort} sortConfig={sortConfig} />
								<SortableHeader columnKey="type" label="Type" onSort={handleSort} sortConfig={sortConfig} />
								<SortableHeader columnKey="partyName" label="Party" onSort={handleSort} sortConfig={sortConfig} />
								<SortableHeader columnKey="inflow" label="Inflow" onSort={handleSort} sortConfig={sortConfig} />
								<SortableHeader columnKey="outflow" label="Outflow" onSort={handleSort} sortConfig={sortConfig} />
								<SortableHeader columnKey="balance" label="Balance" onSort={handleSort} sortConfig={sortConfig} />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
							{pagination.currentData.map((r, idx) => (
								<tr key={r.id || idx} className="hover:bg-gray-50">
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(r.date)}</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-blue-700">{r.docNo || '-'}</td>
									<td className="px-6 py-4 whitespace-nowrap"><span className={`px-2 py-1 rounded text-xs font-medium ${r.inflow>0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{r.type}</span></td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.partyName || '-'}</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{r.inflow>0?formatCurrency(r.inflow):'-'}</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{r.outflow>0?formatCurrency(r.outflow):'-'}</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 font-semibold">{formatCurrency(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden p-3 space-y-3">
            <div className="text-xs font-bold text-gray-800 uppercase tracking-wide px-0.5">
              Transactions
            </div>
            {pagination.currentData.length === 0 ? (
              <p className="text-center text-sm text-gray-500 py-6">No rows on this page.</p>
            ) : (
              pagination.currentData.map((r, idx) => {
                const isIn = r.inflow > 0;
                return (
                  <div
                    key={r.id || idx}
                    className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200/90 shadow-sm overflow-hidden"
                  >
                    <div className="p-3.5">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div
                          className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-md ${
                            isIn
                              ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'
                              : 'bg-gradient-to-br from-red-500 to-rose-600 text-white'
                          }`}
                        >
                          {isIn ? (
                            <TrendingUp className="w-4 h-4" aria-hidden />
                          ) : (
                            <TrendingDown className="w-4 h-4" aria-hidden />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <Calendar className="w-3 h-3 flex-shrink-0" />
                            {formatDate(r.date)}
                          </div>
                          <div className="text-sm font-bold text-blue-800 truncate mt-0.5">{r.docNo || '—'}</div>
                          <span
                            className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              isIn ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {r.type}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2.5 pt-2.5 border-t border-slate-100 text-sm text-gray-800">
                        <span className="text-gray-500 text-xs font-medium uppercase tracking-wide">Party</span>
                        <div className="font-medium truncate">{r.partyName || '—'}</div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-3">
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-2 border border-green-100 text-center">
                          <div className="text-[9px] text-green-700 font-semibold uppercase">In</div>
                          <div className="text-xs font-bold text-green-800 truncate">
                            {r.inflow > 0 ? formatCurrency(r.inflow) : '—'}
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg p-2 border border-red-100 text-center">
                          <div className="text-[9px] text-red-700 font-semibold uppercase">Out</div>
                          <div className="text-xs font-bold text-red-800 truncate">
                            {r.outflow > 0 ? formatCurrency(r.outflow) : '—'}
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-lg p-2 border border-blue-100 text-center">
                          <div className="text-[9px] text-blue-700 font-semibold uppercase">Bal</div>
                          <div className="text-xs font-bold text-blue-900 truncate">{formatCurrency(r.balance)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <PaginationControls {...pagination} />
        </div>

			{filtered.length === 0 && !loading && (
				<div className="text-center py-8"><div className="text-gray-500 text-lg mb-2">No entries found</div></div>
      )}
    </div>
  );
};

export default CashFlowReport; 

